import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  deleteMileageEntry,
  deleteMileageEntryFailure,
  deleteMileageEntrySuccess,
  loadMileageData,
  loadMileageDataFailure,
  loadMileageDataSuccess,
  saveMileageEntries,
  saveMileageEntriesFailure,
  saveMileageEntriesSuccess,
} from './mileage.actions';
import { selectRawMileageData } from './mileage.selectors';
import { MileageService } from '../services/mileage.service';

@Injectable()
export class MileageEffects {
  constructor(
    private actions$: Actions,
    private mileageService: MileageService,
    private toastController: ToastController,
    private store: Store
  ) {}

  loadMileageData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMileageData),
      switchMap(({ vanId }) =>
        this.mileageService.getMileageData(vanId).pipe(
          map((data) => loadMileageDataSuccess({ data })),
          catchError((error) =>
            of(loadMileageDataFailure({ error: error.message }))
          )
        )
      )
    )
  );

  saveMileageEntries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveMileageEntries),
      switchMap(({ vanId, entries }) =>
        this.mileageService.upsertMileageData(vanId, { entries }).pipe(
          map(() => saveMileageEntriesSuccess({ vanId })),
          catchError((error) =>
            of(saveMileageEntriesFailure({ error: error.message }))
          )
        )
      )
    )
  );

  saveMileageEntriesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveMileageEntriesSuccess),
        tap(async () => {
          const toast = await this.toastController.create({
            message: 'Mileage entry saved successfully',
            duration: 2000,
            color: 'success',
            position: 'bottom',
          });
          await toast.present();
        })
      ),
    { dispatch: false }
  );

  // Reload mileage entries after successful save
  reloadAfterSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveMileageEntriesSuccess),
      map(({ vanId }) => loadMileageData({ vanId }))
    )
  );

  saveMileageEntriesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveMileageEntriesFailure),
        tap(async ({ error }) => {
          const toast = await this.toastController.create({
            message: `Failed to save mileage entry: ${error}`,
            duration: 3000,
            color: 'danger',
            position: 'bottom',
          });
          await toast.present();
        })
      ),
    { dispatch: false }
  );

  deleteMileageEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteMileageEntry),
      withLatestFrom(this.store.select(selectRawMileageData)),
      switchMap(([{ vanId, entryId }, currentData]) => {
        if (!currentData) {
          return of(
            deleteMileageEntryFailure({
              error: 'No mileage data available to delete from',
            })
          );
        }

        const updatedEntries = currentData.entries.filter(
          (entry) => entry.id !== entryId
        );

        return this.mileageService
          .upsertMileageData(vanId, { entries: updatedEntries })
          .pipe(
            map(() => deleteMileageEntrySuccess()),
            catchError((error) =>
              of(deleteMileageEntryFailure({ error: error.message }))
            )
          );
      })
    )
  );

  deleteMileageEntrySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteMileageEntrySuccess),
        tap(async () => {
          const toast = await this.toastController.create({
            message: 'Mileage entry deleted successfully',
            duration: 2000,
            color: 'success',
            position: 'bottom',
          });
          await toast.present();
        })
      ),
    { dispatch: false }
  );

  deleteMileageEntryFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteMileageEntryFailure),
        tap(async (action) => {
          const toast = await this.toastController.create({
            message: action.error || 'Failed to delete mileage entry.',
            duration: 7000,
            color: 'warning',
            position: 'bottom',
          });
          await toast.present();
        })
      ),
    { dispatch: false }
  );
}
